<template>
    <div>
        <Toolbar>
            <BackButton :to="__('general.offers')"
                        :href="$route('offers')"
                        :prevent-leave="offerSettings.isDirty"
            />
        </Toolbar>

        <Card>
            <template v-slot:icon>
                <i class="fas fa-cogs"/>
            </template>
            <template v-slot:title>
                {{ __('general.settings') }}
            </template>
            <template v-slot:description>
                {{ __('offers.manage_base_settings') }}
            </template>

            <div class="row">
                <div class="col-lg-6">
                    <FormGroupNew :label="__('offers.name')"
                                  :description="__('offers.name_description')"
                                  required
                                  no-margin>
                        <input type="text"
                               class="form-control"
                               :class="{'form-control-solid': isSystemGenerated}"
                               v-model="offerSettings.name"
                               :disabled="isSystemGenerated">
                    </FormGroupNew>
                </div>
                <div class="col-lg-6">
                    <div v-if="isSystemGenerated" class="alert alert-custom alert-light-warning fade show mb-0"
                         role="alert">
                        <div class="alert-icon">
                            <i class="fas fa-exclamation-circle"/>
                        </div>
                        <div class="alert-text">
                            <h4 class="alert-heading">{{ __('offers.generated') }}</h4>
                            <p>{{ __('offers.generated_no_edit_description') }}</p>
                        </div>
                    </div>
                    <div v-else>
                        <FormGroupNew :label="__('offers.time_limit')"
                                      :description="__('offers.time_limit_description')"
                                      no-margin
                        >
                            <div class="input-group">
                                <input class="form-control"
                                       type="datetime-local"
                                       :value="expireDateValue"
                                       @input="offerSettings.expired_at = $event.target.value"
                                >
                                <div v-if="offerSettings.expired_at" class="input-group-append">
                                    <button class="btn btn-outline-secondary btn-hover-danger"
                                            @click="offerSettings.expired_at = null">
                                        <i class="fas fa-trash ml-1"/>
                                    </button>
                                </div>
                            </div>
                        </FormGroupNew>
                    </div>
                </div>
            </div>

            <template v-slot:footer>
                <div v-if="!isSystemGenerated && offerSettings.isDirty" class="mt-3">
                    <button class="btn btn-primary" @click="saveOfferSettings">
                        <i class="fas fa-save"/>
                        {{ __('actions.save_thing', {thing: __('general.settings')}) }}
                    </button>
                </div>
            </template>
        </Card>

        <Card class="mt-3">
            <template v-slot:icon>
                <i class="fas fa-tshirt"/>
            </template>
            <template v-slot:title>
                {{ __('general.articles') }} ({{ offer.articles_count }})
            </template>
            <template v-slot:description>
                {{ __('offers.add_or_remove_articles') }}
            </template>
            <template v-slot:toolbar>
                <Link as="button"
                      class="btn btn-default btn-hover-primary"
                      :href="$route('offers.articles.edit', offer.id)"
                >
                    <i class="fas fa-edit"/>
                    {{ __('actions.manage_thing', {thing: __('general.article')}) }}
                </Link>
            </template>

            <div v-if="offer.products.isEmpty()" class="text-muted">
                {{ __('general.things.none_present_yet', {things: __('general.articles')}) }}
            </div>
            <div v-else class="d-flex flex-row gap-2 align-items-center">
                <div v-for="article in offer.products.slice(0, 6)"
                     :key="article.ean"
                     class="rounded border border-light-light overflow-hidden"
                     style="width: 64px; height: 64px;"
                >
                    <img class="w-100 h-100 p-2" :src="article.image" :alt="article.name" style="object-fit: contain;">
                </div>
                <div v-if="offer.articles_count > 6">
                    + {{ offer.articles_count - 6 }}
                </div>
            </div>
        </Card>

        <Card class="mt-3">
            <template v-slot:icon>
                <i class="fas fa-building"/>
            </template>
            <template v-slot:title>
                {{ __('general.stores') }} ({{ offer.stores_count }})
            </template>
            <template v-slot:description>
                {{ __('offers.add_or_remove_stores') }}
            </template>
            <template v-slot:toolbar>
                <Link as="button"
                      class="btn btn-default btn-hover-primary"
                      :href="$route('offers.stores.edit', offer.id)"
                >
                    <i class="fas fa-edit"/>
                    {{ __('actions.manage_thing', {thing: __('general.stores')}) }}
                </Link>
            </template>

            <div v-if="offer.stores_count === 0" class="text-muted">
                {{ __('general.things.none_present_yet', {things: __('general.stores')}) }}
            </div>
            <table v-else>
                <tbody>
                <tr v-for="storeName in offer.stores">
                    <td>{{ storeName }}</td>
                </tr>
                </tbody>
            </table>
        </Card>
    </div>
</template>

<script>
import Errors from '@components/Errors'
import ToggleModel from '@components/ToggleModel'
import Toolbar from "@components/Toolbar.vue";
import BackButton from "@components/BackButton.vue";
import Card from "@components/Cards/Card.vue";
import FormGroupNew from "@components/Form/FormGroupNew.vue";
import { Link } from '@inertiajs/vue2';

export default {
    components: {
        FormGroupNew,
        Card,
        BackButton,
        Toolbar,
        Errors,
        ToggleModel,
        Link
    },

    props: {
        offer: Object,
    },

    data() {
        return {
            offerSettings: this.$inertia.form({
                name: this.offer.name,
                expired_at: this.offer.expired_at,
                show_on_start_page: true
            }),
        }
    },

    methods: {
        saveOfferSettings() {
            this.offerSettings.patch(this.$route('offers.update', this.offer.id))
        }
    },

    computed: {
        isSystemGenerated() {
            return this.offer.created_by === 'system'
        },

        expireDateValue() {
            if (this.offer.expired_at && this.offerSettings.expired_at !== null) {
                return new Date(this.offer.expired_at).toDateTimeInputFormat();
            }

            return this.offerSettings.expired_at;
        }
    },
}
</script>

<style scoped>
.demo-trigger .demo {
    display: none;
    position: absolute;
    z-index: 1000;
    margin-top: .4em;
}

.demo-trigger:hover .demo {
    display: block;
}

.demo-trigger .demo img {
    height: 24em;
}
</style>
